import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'fongpt-enabled-connectors';
const CHANGE_EVENT = 'fongpt-connectors-changed';

// 'microsoft' — waiting for approval from FON.
const CONNECTOR_IDS = ['googleDrive', 'github'] as const;

type ConnectorId = (typeof CONNECTOR_IDS)[number];
type ConnectorPreferences = Record<ConnectorId, boolean>;

const DEFAULT_PREFERENCES: ConnectorPreferences = {
  googleDrive: true,
  github: false
};

function loadPreferences(): ConnectorPreferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) || '{}'
    ) as Partial<ConnectorPreferences>;

    return CONNECTOR_IDS.reduce(
      (preferences, connector) => ({
        ...preferences,
        [connector]:
          typeof stored[connector] === 'boolean'
            ? stored[connector] === true
            : DEFAULT_PREFERENCES[connector]
      }),
      { ...DEFAULT_PREFERENCES }
    );
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function savePreferences(preferences: ConnectorPreferences) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // Leave the preference in memory if local storage is unavailable.
  }
}

export function useConnectorPreferences() {
  const [enabledConnectors, setEnabledConnectors] =
    useState<ConnectorPreferences>(loadPreferences);

  useEffect(() => {
    const syncPreferences = () => setEnabledConnectors(loadPreferences());
    window.addEventListener(CHANGE_EVENT, syncPreferences);
    return () => window.removeEventListener(CHANGE_EVENT, syncPreferences);
  }, []);

  const setConnectorEnabled = useCallback(
    (connector: ConnectorId, enabled: boolean) => {
      setEnabledConnectors((current) => {
        const next = { ...current, [connector]: enabled };
        savePreferences(next);
        return next;
      });
    },
    []
  );

  return { enabledConnectors, setConnectorEnabled };
}

export type { ConnectorId };
